"use client";

/**
 * Per-project chat switcher — a compact floating control at the top of
 * the chat side panel. Lists the chats attached to the current project
 * (most-recently-attached first) and switches the panel's `?chat=` param
 * on select. Renders nothing off a `/projects/{id}` route or when the
 * project has no attached chats, so it can be dropped into the panel
 * unconditionally.
 */

import React from "react";
import { useAtomValue } from "jotai";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ChevronDown, MessageSquare, Check } from "lucide-react";
import { useNode } from "@sourcepilot/framework/relationship-manager";
import { nodeAtomFamily, graphStoreJotaiStore } from "@sourcepilot/framework/graph-store";
import {
  useProjectChats,
  type ProjectChat,
} from "@sourcepilot/ui/framework/data/hooks/use-project-chats";

const projectIdOf = (pathname: string): string | null =>
  pathname.match(/^\/projects\/([^/]+)/)?.[1] ?? null;

function threadName(task: any, thread: any): string {
  return (thread?.name as string) || (task?.task_name as string) || "Untitled Chat";
}

/** A row in the dropdown — own component so `useNode(thread)` is a valid
 *  unconditional hook per item. */
function ChatRow({
  chat,
  active,
  onSelect,
}: {
  chat: ProjectChat;
  active: boolean;
  onSelect: () => void;
}) {
  const thread = useNode<any>(chat.task?.thread);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
        active ? "font-medium text-gray-900" : "text-gray-700"
      }`}
    >
      <MessageSquare className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span className="flex-1 truncate">{threadName(chat.task, thread)}</span>
      {active && <Check className="h-3.5 w-3.5 shrink-0 text-blue-600" />}
    </button>
  );
}

export function ProjectChatSwitcher({ inline = false }: { inline?: boolean }) {
  const pathname = usePathname() || "";
  const router = useRouter();
  const searchParams = useSearchParams();
  const projectId = projectIdOf(pathname);
  const { chats } = useProjectChats(projectId);
  const [open, setOpen] = React.useState(false);

  // Project title for the trigger — read from the GraphStore (the project
  // page's DAGLayout already loaded the Project node).
  const projectNode = useAtomValue(
    React.useMemo(() => nodeAtomFamily(projectId ?? "__none__"), [projectId]),
    { store: graphStoreJotaiStore },
  ) as any;

  const currentChatId = searchParams.get("chat");

  // Hooks above the early return so order is stable.
  if (!projectId || chats.length === 0) return null;

  const current =
    chats.find((c) => String(c.task?.id) === currentChatId) ?? chats[0];

  const select = (chat: ProjectChat) => {
    setOpen(false);
    const id = String(chat.task?.id ?? "");
    if (!id || id === currentChatId) return;
    const params = new URLSearchParams(window.location.search);
    params.set("chat", id);
    const cls = chat.task?._label;
    if (cls && cls !== "ChatTask") params.set("agent", String(cls));
    else params.delete("agent");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  // Only a dropdown when there's more than one chat to switch between;
  // a single chat just shows the project title as static context.
  const multiple = chats.length > 1;

  return (
    <div className={inline ? "relative min-w-0" : "absolute right-2 top-0 z-30"}>
      <button
        type="button"
        onClick={multiple ? () => setOpen((v) => !v) : undefined}
        className={`flex max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-white/95 px-2.5 py-1 text-sm shadow-sm backdrop-blur ${
          multiple ? "cursor-pointer hover:bg-gray-50" : "cursor-default"
        }`}
        title={multiple ? "Switch chat" : undefined}
      >
        <MessageSquare className="h-4 w-4 shrink-0 text-gray-400" />
        <span className="min-w-0 max-w-[200px] truncate font-medium text-gray-900">
          {(projectNode?.name as string) || "Project"}
        </span>
        {multiple && (
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {open && multiple && (
        <>
          {/* click-away */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 max-h-72 w-64 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {chats.map((c) => (
              <ChatRow
                key={String(c.task?.id)}
                chat={c}
                active={String(c.task?.id) === String(current.task?.id)}
                onSelect={() => select(c)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
